import React, { useEffect, useState, createContext, useContext } from 'react';
export interface CartItem {
  id: string;
  type: 'softplay' | 'bouncehouse' | 'photobooth';
  packageName: string;
  packageValue: string;
  price: string;
  wantsCustomization?: 'yes' | 'no';
  customizationDetails?: string;
  selectedBounceHouse?: string; // For Mega Fancy Tot
}
interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'id'>) => {
    success: boolean;
    message?: string;
    showUpgradeOption?: boolean;
  };
  removeFromCart: (id: string) => void;
  updateCartItem: (id: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;
  itemCount: number;
  totalPrice: number;
  canAddItem: (item: Omit<CartItem, 'id'>) => {
    canAdd: boolean;
    message?: string;
    showUpgradeOption?: boolean;
  };
  upgradeToGigantatot: () => void;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
// Package conflict groups - packages that share materials
const COLORFUL_PACKAGES = ['tiny-tot', 'mega-tot', 'gigantatot'];
const LUXURY_PACKAGES = ['fancy-tot', 'mega-fancy-tot'];
// Bounce house inclusions for each package
const PACKAGE_BOUNCE_HOUSES: Record<string, string[]> = {
  'fancy-tot': ['medium-white-bounce-house'],
  'tiny-tot': ['medium-bounce-house'],
  'mega-tot': ['combo-bounce-house'],
  gigantatot: ['medium-bounce-house', 'combo-bounce-house']
  // mega-fancy-tot is dynamic based on user selection
};
export const CartProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [items, setItems] = useState<CartItem[]>([]);
  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('toddlerTimeCart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error);
      }
    }
  }, []);
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('toddlerTimeCart', JSON.stringify(items));
  }, [items]);
  const canAddItem = (item: Omit<CartItem, 'id'>): {
    canAdd: boolean;
    message?: string;
    showUpgradeOption?: boolean;
  } => {
    // Rule 1: Cannot add the same package twice
    const duplicatePackage = items.find(cartItem => cartItem.packageValue === item.packageValue);
    if (duplicatePackage) {
      return {
        canAdd: false,
        message: `You already have "${item.packageName}" in your cart. We only have one of each item available.`
      };
    }
    // Rule 2: Cannot mix colorful packages (they share materials)
    if (COLORFUL_PACKAGES.includes(item.packageValue)) {
      const hasColorfulPackage = items.find(cartItem => COLORFUL_PACKAGES.includes(cartItem.packageValue));
      if (hasColorfulPackage) {
        // Special case: Suggest Gigantatot if trying to add Tiny Tot + Mega Tot
        if (item.packageValue === 'tiny-tot' && hasColorfulPackage.packageValue === 'mega-tot' || item.packageValue === 'mega-tot' && hasColorfulPackage.packageValue === 'tiny-tot') {
          return {
            canAdd: false,
            message: `You cannot combine "${item.packageName}" with "${hasColorfulPackage.packageName}" as they share materials. Would you like the Gigantatot package instead? It includes everything from both packages!`,
            showUpgradeOption: true
          };
        }
        return {
          canAdd: false,
          message: `You cannot combine "${item.packageName}" with "${hasColorfulPackage.packageName}" as they share the same materials.`
        };
      }
    }
    // Cannot mix luxury packages
    if (LUXURY_PACKAGES.includes(item.packageValue)) {
      const hasLuxuryPackage = items.find(cartItem => LUXURY_PACKAGES.includes(cartItem.packageValue));
      if (hasLuxuryPackage) {
        return {
          canAdd: false,
          message: `You cannot combine "${item.packageName}" with "${hasLuxuryPackage.packageName}" as they share the same materials.`
        };
      }
    }
    // Rule 4: Cannot add bounce house if it's included in a package
    if (item.type === 'bouncehouse') {
      // Check all packages in cart for included bounce houses
      for (const cartItem of items) {
        if (cartItem.type === 'softplay') {
          const includedBounceHouses = PACKAGE_BOUNCE_HOUSES[cartItem.packageValue] || [];
          // For Mega Fancy Tot, check the selected bounce house
          if (cartItem.packageValue === 'mega-fancy-tot' && cartItem.selectedBounceHouse) {
            includedBounceHouses.push(cartItem.selectedBounceHouse);
          }
          if (includedBounceHouses.includes(item.packageValue)) {
            return {
              canAdd: false,
              message: `The "${item.packageName}" is already included with your "${cartItem.packageName}" package.`
            };
          }
        }
      }
    }
    return {
      canAdd: true
    };
  };
  const addToCart = (item: Omit<CartItem, 'id'>): {
    success: boolean;
    message?: string;
    showUpgradeOption?: boolean;
  } => {
    const validation = canAddItem(item);
    if (!validation.canAdd) {
      return {
        success: false,
        message: validation.message,
        showUpgradeOption: validation.showUpgradeOption
      };
    }
    const newItem: CartItem = {
      ...item,
      id: `${item.type}-${item.packageValue}-${Date.now()}`
    };
    setItems(prev => [...prev, newItem]);
    return {
      success: true
    };
  };
  const removeFromCart = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };
  const updateCartItem = (id: string, updates: Partial<CartItem>) => {
    setItems(prev => prev.map(item => item.id === id ? {
      ...item,
      ...updates
    } : item));
  };
  const clearCart = () => {
    setItems([]);
  };
  const upgradeToGigantatot = () => {
    // Remove any Tiny Tot or Mega Tot packages
    const filteredItems = items.filter(item => item.packageValue !== 'tiny-tot' && item.packageValue !== 'mega-tot');
    // Add Gigantatot
    const gigantatotItem: CartItem = {
      id: `softplay-gigantatot-${Date.now()}`,
      type: 'softplay',
      packageName: 'Gigantatot',
      packageValue: 'gigantatot',
      price: '$500'
    };
    setItems([...filteredItems, gigantatotItem]);
  };
  const itemCount = items.length;
  const totalPrice = items.reduce((total, item) => {
    // Extract numeric value from price string (e.g., "$325" -> 325)
    const priceMatch = item.price.match(/\d+/);
    const price = priceMatch ? parseInt(priceMatch[0]) : 0;
    return total + price;
  }, 0);
  return <CartContext.Provider value={{
    items,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    itemCount,
    totalPrice,
    canAddItem,
    upgradeToGigantatot
  }} data-id="element-491">
      {children}
    </CartContext.Provider>;
};
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};